import { useCallback, useEffect, useState } from "react";
import { errMsg } from "@shared/api/errors";
import { isoCertificatesApi } from "../api/certificados-iso";
import type {
  IsoCertificateCode,
  IsoCertificateSlot,
} from "../types/certificados-iso";

export function useIsoCertificates() {
  const [slots, setSlots] = useState<IsoCertificateSlot[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingCode, setUploadingCode] = useState<IsoCertificateCode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await isoCertificatesApi.list();
      setSlots(response.data);
      setCanManage(response.can_manage);
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudieron cargar los certificados ISO."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const upload = useCallback(async (codigo: IsoCertificateCode, file: File) => {
    setUploadingCode(codigo);
    setError(null);

    try {
      const updated = await isoCertificatesApi.upload(codigo, file);
      setSlots((current) => {
        const exists = current.some((slot) => slot.codigo === codigo);
        if (!exists) {
          return [...current, updated];
        }

        return current.map((slot) => (slot.codigo === codigo ? updated : slot));
      });
      return true;
    } catch (requestError) {
      setError(errMsg(requestError, "No se pudo guardar el certificado ISO."));
      return false;
    } finally {
      setUploadingCode(null);
    }
  }, []);

  return {
    slots,
    canManage,
    loading,
    uploadingCode,
    error,
    load,
    upload,
    clearError: () => setError(null),
  };
}
